import {Injectable} from '@angular/core';
import {SpatialUtilService} from './spatial-util.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {EMPTY, Observable, catchError, concatMap, of, switchMap, tap, throwError} from 'rxjs';
import {AlertModalService} from '../../common/component/modal/alert-modal/alert-modal.service';
import {NgEventBus} from 'ng-event-bus';
import {Result, FeaturesDataSet} from '../../common/models/feature-apply-results.model';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';
import {MapService} from '../services/map.service';

@Injectable({
    providedIn: 'root'
})
export class FeaturesApplyInsertService {
    title = 'FeaturesApplyInsertService';

    constructor(
        private spatialUtilService: SpatialUtilService,
        private planModelService: PlanModelService,
        private alertModalService: AlertModalService,
        private eventBus: NgEventBus,
        private mapService: MapService
    ) {}

    insertFeatures(results: Result[]) {
        const promises: Observable<any>[] = [];
        const observable: Observable<any> = of(null);

        const featuresDataSet: FeaturesDataSet = {};

        Object.keys(results).forEach(key => {
            switch (results[key].paramUrl) {
                case 'results/Point':
                    const pointTypes = this.spatialUtilService.getPointTypes(results[key].featureResult.value.features);
                    const controlPointTypes = this.spatialUtilService.getControlPointTypes(results[key].featureResult.value.features);
                    promises.push(
                        this.prepareInsertFeaturesByType(this.spatialUtilService.getPointMapLayer(), pointTypes).pipe(
                            tap(insertedFeatures => {
                                featuresDataSet.point = insertedFeatures;
                            }),
                            switchMap(() => {
                                if (controlPointTypes.length > 0) {
                                    return this.prepareInsertFeaturesByType(
                                        this.spatialUtilService.getControlPointMapLayer(),
                                        controlPointTypes
                                    ).pipe(
                                        tap(data => {
                                            featuresDataSet.controlPoint = data;
                                        })
                                    );
                                }
                                return of(null);
                            }),
                            catchError(() => {
                                console.error('Error in results/Point case');
                                return throwError(() => {
                                    new Error();
                                });
                            })
                        )
                    );
                    break;
                case 'results/Line':
                    promises.push(
                        this.prepareInsertFeaturesByType(this.spatialUtilService.getLineMapLayer(), results[key].featureResult.value.features).pipe(
                            tap(insertedFeatures => {
                                featuresDataSet.line = insertedFeatures;
                            }),
                            catchError(() => {
                                console.error('Error in results/Line case');
                                return throwError(() => {
                                    new Error();
                                });
                            })
                        )
                    );
                    break;
                case 'results/Polygon':
                    promises.push(
                        this.prepareInsertFeaturesByType(
                            this.spatialUtilService.getPolygonMapLayer(),
                            results[key].featureResult.value.features
                        ).pipe(
                            tap(insertedFeatures => {
                                featuresDataSet.polygon = insertedFeatures;
                            }),
                            catchError(() => {
                                console.error('Error in results/Polygon case');
                                return throwError(() => {
                                    new Error();
                                });
                            })
                        )
                    );
                    break;
                default:
                    break;
            }
        });

        return forkJoinDefaultIfEmpty(promises).pipe(
            switchMap(() => {
                if (featuresDataSet.point && featuresDataSet.line && featuresDataSet.polygon) {
                    return this.prepareInsertFeaturesByType(this.spatialUtilService.getLotMapLayer(), results['Lot'].featureResult.value.features);
                } else {
                    return throwError(() => {
                        new Error();
                    });
                }
            }),
            tap(() => {
                this.mapService.moveToPlanExtent();
            }),
            switchMap(() => {
                return this.prepareInsertFeaturesByType(this.spatialUtilService.getAngleMapLayer(), results['Angle'].featureResult.value.features);
            }),
            catchError(() => {
                return throwError(() => {
                    console.error(`${this.title} : insertFeatures - Error updating features.`);
                    new Error();
                });
            })
        );
    }

    prepareInsertFeaturesByType(featureLayer: any, features: any) {
        console.debug(this.title + ' : prepareInsertFeaturesByType - TYPE: ' + featureLayer.name + ' SIZE: ' + (features ? features.length : '0'));

        if (features && features.length > 0) {
            return this.insertFeaturesByType(featureLayer, features, (results: any) => {
                return of(results);
            });
        } else {
            return of([]);
        }
    }

    insertFeaturesByType(featureLayer, features, featuresCreationCallback?: (results: any[]) => void) {
        console.debug(this.title + ' : insertFeaturesByType - TYPE: ' + featureLayer.name + ' SIZE: ' + (features ? features.length : '0'));

        if (features.length === 0) {
            if (featuresCreationCallback) {
                featuresCreationCallback([]);
            }
            return of([]);
        }

        const featureMap = features.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        features = features
            .map(f => f.attributes.id)
            .filter((v, i, a) => a.indexOf(v) === i)
            .map(v => featureMap[v]);

        const featureLayerType = this.spatialUtilService.getFeatureLayerName(featureLayer);

        features.forEach(feature => {
            feature.attributes.guid = this.planModelService.getGuid();

            if (feature.geometry && feature.geometry.type === 'point') {
                if (feature.attributes.hasOwnProperty('x')) {
                    feature.attributes.x = feature.geometry.x;
                }
                if (feature.attributes.hasOwnProperty('y')) {
                    feature.attributes.y = feature.geometry.y;
                }
            }
        });

        return this.spatialUtilService.applyEdits(featureLayer, features, null, null).pipe(
            concatMap(res => {
                console.debug(this.title + ' : insertFeaturesByType adds - TYPE: ' + featureLayer.name + ' - RESULT: ', res.adds);
                const unsuccessfulFeatures = res.adds.filter(v => v.error);

                if (unsuccessfulFeatures.length === 0) {
                    if (featureLayerType !== 'angle' && featureLayerType !== 'annotation' && featureLayerType !== 'polygon') {
                        features.forEach(feature => {
                            const metaData = {feature: feature, featureType: featureLayerType};
                            this.eventBus.cast('createAnnotation', metaData);
                        });
                    }
                    if (featuresCreationCallback) {
                        featuresCreationCallback(features);
                    }
                    return of(features);
                } else {
                    return throwError(() => {
                        new Error();
                    });
                }
            }),
            catchError(() => {
                const errorMsg = 'Error inserting features';
                this.alertModalService.createAlertMessage(errorMsg, 'OK');
                if (featuresCreationCallback) featuresCreationCallback(['error']);
                return throwError(() => {
                    new Error();
                });
            })
        );
    }
}
